import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Database, Download, FileArchive, FileSpreadsheet, Upload } from "lucide-react";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { erpApi } from "../services/erpApi";

const csvTypes = [
  { value: "patients", label: "Patient List", helper: "Purane patients ki full profile aur registration date save karega." },
  { value: "samples", label: "Lab Test List", helper: "Purane test/sample records patient mobile number se jodega aur back date maintain karega." },
  { value: "bills", label: "Bill List", helper: "Purane bills, payment records, invoice number aur bill date import karega." },
  { value: "expenses", label: "Expense List", helper: "Rent, salary, lab payment aur other kharche import karega." },
];

const manifestTypes = [
  { value: "report_pdfs", label: "Patient Report PDF List", helper: "Purani report PDF links ko patient ke test ke saath jodega." },
  { value: "bill_pdfs", label: "Bill PDF List", helper: "Purane bill/invoice PDF links ko bill ke saath jodega." },
];

function ResultBox({ title, result }) {
  const errors = result?.errors || [];
  const ready = result?.ready_to_import ?? result?.ready_to_attach ?? 0;
  const saved = result?.inserted ?? result?.attached ?? 0;

  return (
    <Card className="border border-gray-200">
      <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {!result ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-sm text-hospital-text-muted">
            Abhi koi file check nahi hui. Pehle file choose karein, phir <span className="font-semibold text-hospital-text-main">Check File</span> dabayein.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-lg bg-gray-50 p-3">
                <p className="text-xs text-hospital-text-muted">Total rows</p>
                <p className="text-xl font-bold">{result.rows || 0}</p>
              </div>
              <div className="rounded-lg bg-emerald-50 p-3">
                <p className="text-xs text-emerald-700">Ready</p>
                <p className="text-xl font-bold text-emerald-800">{ready}</p>
              </div>
              <div className="rounded-lg bg-hospital-primary-light p-3">
                <p className="text-xs text-hospital-primary">Saved</p>
                <p className="text-xl font-bold text-hospital-primary">{saved}</p>
              </div>
              <div className="rounded-lg bg-red-50 p-3">
                <p className="text-xs text-red-700">Problems</p>
                <p className="text-xl font-bold text-red-800">{errors.length}</p>
              </div>
            </div>

            {result.dry_run ? (
              <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
                File check ho gayi. Agar problems 0 hain, ab <span className="font-semibold">Save to ParshCare</span> dabayein.
              </div>
            ) : (
              <div className="flex items-center gap-2 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800">
                <CheckCircle2 className="h-4 w-4" />
                Data ParshCare me save ho gaya.
              </div>
            )}

            {errors.length > 0 && (
              <div className="rounded-lg border border-red-100">
                <div className="border-b border-red-100 bg-red-50 px-4 py-2 text-sm font-semibold text-red-800">Fix these rows</div>
                <div className="max-h-52 overflow-auto">
                  {errors.map((error, index) => (
                    <div key={index} className="grid grid-cols-[80px_1fr] gap-2 border-b border-red-50 px-4 py-2 text-sm last:border-0">
                      <span className="font-medium text-red-700">Row {error.row}</span>
                      <span className="text-hospital-text-secondary">{error.message}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function ImportCenter() {
  const [csvType, setCsvType] = useState("patients");
  const [manifestType, setManifestType] = useState("report_pdfs");
  const [csvFile, setCsvFile] = useState(null);
  const [manifestFile, setManifestFile] = useState(null);
  const [csvResult, setCsvResult] = useState(null);
  const [manifestResult, setManifestResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const downloadTemplate = async (entity) => {
    const response = await API.get(erpApi.importTemplateUrl(entity), { responseType: "blob" });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${entity}_template.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const submitCsv = async (dryRun) => {
    if (!csvFile) return toast.error("Pehle CSV file choose karein");
    const formData = new FormData();
    formData.append("file", csvFile);
    setLoading(true);
    try {
      const res = await erpApi.importCsv(csvType, formData, dryRun);
      setCsvResult(res.data);
      toast.success(dryRun ? "File check ho gayi" : "Data save ho gaya");
    } catch (error) {
      toast.error(error.response?.data?.detail || "File import nahi hui");
      setCsvResult(error.response?.data || { errors: [{ row: "-", message: "File import nahi hui" }] });
    } finally {
      setLoading(false);
    }
  };

  const submitManifest = async (dryRun) => {
    if (!manifestFile) return toast.error("Pehle PDF list CSV choose karein");
    const formData = new FormData();
    formData.append("file", manifestFile);
    setLoading(true);
    try {
      const res = manifestType === "report_pdfs"
        ? await erpApi.importReportPdfs(formData, dryRun)
        : await erpApi.importBillPdfs(formData, dryRun);
      setManifestResult(res.data);
      toast.success(dryRun ? "PDF list check ho gayi" : "PDF links attach ho gaye");
    } catch (error) {
      toast.error(error.response?.data?.detail || "PDF list import nahi hui");
      setManifestResult(error.response?.data || { errors: [{ row: "-", message: "PDF list import nahi hui" }] });
    } finally {
      setLoading(false);
    }
  };

  const currentCsv = csvTypes.find((item) => item.value === csvType);
  const currentManifest = manifestTypes.find((item) => item.value === manifestType);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-manrope text-2xl font-bold">Old Data Import</h1>
        <p className="text-sm text-hospital-text-muted">Excel/Tally se nikali hui CSV file yahan se ParshCare me save karein.</p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card className="border border-gray-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-hospital-primary-light p-3 text-hospital-primary"><FileSpreadsheet className="h-5 w-5" /></div>
              <div><p className="font-semibold">1. Excel ko CSV banayein</p><p className="text-sm text-hospital-text-muted">Excel/Tally me Save as CSV karein.</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-gray-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-amber-50 p-3 text-amber-700"><Database className="h-5 w-5" /></div>
              <div><p className="font-semibold">2. Pehle file check karein</p><p className="text-sm text-hospital-text-muted">Galti wali rows pehle dikhenge.</p></div>
            </div>
          </CardContent>
        </Card>
        <Card className="border border-gray-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="rounded-lg bg-emerald-50 p-3 text-emerald-700"><FileArchive className="h-5 w-5" /></div>
              <div><p className="font-semibold">3. Data save karein</p><p className="text-sm text-hospital-text-muted">Check ke baad final save karein.</p></div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Card className="border border-gray-200">
          <CardHeader><CardTitle className="text-base">Patient, Test, Bill ya Expense Import</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Kis type ka data hai?</Label>
              <Select value={csvType} onValueChange={setCsvType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{csvTypes.map((type) => <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-sm text-hospital-text-muted">{currentCsv?.helper}</p>
            </div>
            <div className="space-y-2">
              <Label>CSV file choose karein</Label>
              <input className="block w-full rounded-md border border-gray-200 bg-white p-2 text-sm" type="file" accept=".csv,text/csv" onChange={(event) => setCsvFile(event.target.files?.[0] || null)} />
              {csvFile && <p className="text-sm text-hospital-text-muted">Selected file: {csvFile.name}</p>}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => downloadTemplate(csvType)}><Download className="mr-2 h-4 w-4" />Blank Format</Button>
              <Button variant="outline" disabled={loading} onClick={() => submitCsv(true)}><Database className="mr-2 h-4 w-4" />Check File</Button>
              <Button className="btn-primary" disabled={loading} onClick={() => submitCsv(false)}><Upload className="mr-2 h-4 w-4" />Save to ParshCare</Button>
            </div>
          </CardContent>
        </Card>
        <ResultBox title="File Check Result" result={csvResult} />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Card className="border border-gray-200">
          <CardHeader><CardTitle className="text-base">Old PDF Link Attach</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Kaunsi PDF list hai?</Label>
              <Select value={manifestType} onValueChange={setManifestType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{manifestTypes.map((type) => <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-sm text-hospital-text-muted">{currentManifest?.helper}</p>
            </div>
            <div className="space-y-2">
              <Label>PDF list CSV choose karein</Label>
              <input className="block w-full rounded-md border border-gray-200 bg-white p-2 text-sm" type="file" accept=".csv,text/csv" onChange={(event) => setManifestFile(event.target.files?.[0] || null)} />
              {manifestFile && <p className="text-sm text-hospital-text-muted">Selected file: {manifestFile.name}</p>}
            </div>
            <div className="rounded-lg bg-gray-50 p-3 text-sm text-hospital-text-muted">
              PDF file direct upload nahi hoti. Pehle PDF ko Drive/Cloudinary/S3 par upload karein, phir CSV me uska link paste karein.
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => downloadTemplate(manifestType)}><Download className="mr-2 h-4 w-4" />Blank Format</Button>
              <Button variant="outline" disabled={loading} onClick={() => submitManifest(true)}><Database className="mr-2 h-4 w-4" />Check PDF List</Button>
              <Button className="btn-primary" disabled={loading} onClick={() => submitManifest(false)}><Upload className="mr-2 h-4 w-4" />Attach PDF Links</Button>
            </div>
          </CardContent>
        </Card>
        <ResultBox title="PDF Link Result" result={manifestResult} />
      </div>
    </div>
  );
}
